import { CircularProgress, FormControlLabel, RadioGroup, Stack, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { openToast } from '../../store/reducers/toastSlice';
import {
    formatCurrency,
    getRemainingPrincipalIntegerDigitCount,
    normalizeConsumerLoanAmountInput,
    regex,
} from '../../utils/helper';
import { useTranslation } from '../../utils/translations';
import { CustomRadio } from '../radio';
import { TextFieldComponent } from '../text-field/text-field-component';
import {
    AMOUNT_MAX_VALUE,
    AMOUNT_PRINCIPAL_THRESHOLD,
    getAmountMaxLength,
    getAmountMaxValue,
    isAmountOverMax,
} from '../validations/validations';

import type { AmountChoice, PaymentRow } from '../../hooks/make-payment/usePaymentRows';
import type { Dispatch, SetStateAction } from 'react';

interface PaymentAmountCellProps {
    row: PaymentRow;
    rowIndex: number;
    paymentMethodType: 'card' | 'bank_account';
    isConsumerLoan: boolean;
    outstandingBalance: number | null;
    hasContractInvoices: boolean;
    getRowCardFeeAmount: (row: PaymentRow, rowIndex: number) => number;
    paymentAmountError: boolean | undefined;
    cardFeesLoading?: boolean;
    selectedCardFunding?: string;
    selectedCardCountry?: string;
    setPaymentRows: Dispatch<SetStateAction<PaymentRow[]>>;
    setPaymentRowErrors: Dispatch<
        SetStateAction<Array<{ bankAccount: boolean; paymentDate: boolean; paymentAmount: boolean }>>
    >;
    setPaymentSubmitErrors: Dispatch<SetStateAction<Array<string | null>>>;
    handleAmountChoiceChange: (index: number, choice: AmountChoice) => void;
    setOtherAmountInputRef: (index: number, el: HTMLInputElement | null) => void;
}

/**
 * The `dueAmount` cell for a payment row: either the simple read-only due-amount display
 * (non-consumer-loan or card) with an optional card-fee caption, or — for consumer-loan bank-account
 * rows — the "invoice vs other amount" radio + editable amount TextField with full input validation.
 */
export const PaymentAmountCell: React.FC<PaymentAmountCellProps> = ({
    row,
    rowIndex: index,
    paymentMethodType,
    isConsumerLoan,
    outstandingBalance,
    hasContractInvoices,
    getRowCardFeeAmount,
    paymentAmountError,
    cardFeesLoading,
    selectedCardFunding,
    selectedCardCountry,
    setPaymentRows,
    setPaymentRowErrors,
    setPaymentSubmitErrors,
    handleAmountChoiceChange,
    setOtherAmountInputRef,
}) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const dueAmountNum = parseFloat(String(row.dueAmount ?? '0')) || 0;
    const scheduledAmountNum = parseFloat(String(row.scheduledAmount ?? '0')) || 0;
    // Commercial (non-consumer-loan) bank-transfer rows fall back to the outstanding balance when
    // there are no open invoices, so principal-only ACH payments still show a payable amount.
    const showsOutstandingBalanceFallback = !isConsumerLoan && paymentMethodType !== 'card';
    const displayDueAmount =
        hasContractInvoices || showsOutstandingBalanceFallback
            ? parseFloat(Math.max(0, dueAmountNum - scheduledAmountNum).toFixed(2))
            : 0;
    const isBalanceFullyCovered =
        hasContractInvoices &&
        outstandingBalance != null &&
        scheduledAmountNum >= outstandingBalance &&
        scheduledAmountNum > 0;

    useEffect(() => {
        if (
            isBalanceFullyCovered &&
            hasContractInvoices &&
            (!isConsumerLoan || paymentMethodType === 'card') === false
        ) {
            dispatch(
                openToast({
                    show: true,
                    isAlertOpen: true,
                    message:
                        "This Contract's Balance Is Already Fully Covered By Open Invoices And Scheduled Payments.",
                    severity: 'error',
                }),
            );
        }
    }, [isBalanceFullyCovered, hasContractInvoices, dispatch, isConsumerLoan, paymentMethodType]);

    // Full (invoice) amount is system-derived: once it is positive, clear any stale amount error so Pay isn't stuck
    // (re-clicking the already-selected "Full" radio doesn't fire onChange).
    useEffect(() => {
        if ((row.amountChoice ?? 'invoice') === 'invoice' && displayDueAmount > 0) {
            setPaymentRowErrors(prev => {
                if (!prev[index]?.paymentAmount) return prev;
                const next = [...prev];
                next[index] = { ...next[index], paymentAmount: false };
                return next;
            });
        }
    }, [row.amountChoice, displayDueAmount, index, setPaymentRowErrors]);

    if (!isConsumerLoan || paymentMethodType === 'card') {
        const isCardSelected = !!row.paymentCardId;
        // Only mark as ineligible if funding is explicitly NOT 'credit'. If undefined, assume eligible (backend validates).
        const isCreditCard = selectedCardFunding === undefined || selectedCardFunding === 'credit';
        // Validate card is US-issued (domestic only)
        const isUSCard = selectedCardCountry === undefined || selectedCardCountry === 'US';
        const isCardIneligible = isCardSelected && (!isCreditCard || !isUSCard);

        // Use the authoritative fee calculation from useCardFees (single source of truth)
        let cellFeeAmount = 0;

        if (paymentMethodType === 'card' && isCardSelected && !isCardIneligible) {
            cellFeeAmount = getRowCardFeeAmount(row, index);
        }

        const hasCellFee = cellFeeAmount > 0 && Number.isFinite(cellFeeAmount);
        const isLoading = paymentMethodType === 'card' && isCardSelected && cardFeesLoading;

        return (
            <Stack>
                <Typography variant="body2" sx={{ pt: 1, pb: hasCellFee || isLoading || isCardIneligible ? 0.25 : 1 }}>
                    {formatCurrency(displayDueAmount)}
                </Typography>
                {isCardIneligible ? (
                    <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 700, pb: 1 }}>
                        {!isCreditCard
                            ? t('makePayment.cardNotEligibleOnlyCredit')
                            : !isUSCard
                              ? t('makePayment.cardNotEligibleUSOnly')
                              : t('makePayment.cardNotEligibleOnlyCredit')}
                    </Typography>
                ) : isLoading ? (
                    <Stack direction="row" alignItems="center" gap={0.5} sx={{ pb: 1 }}>
                        <CircularProgress size={14} sx={{ color: 'error.main' }} />
                        <Typography variant="caption" color="error.main" sx={{ fontWeight: 700 }}>
                            {t('makePayment.cardFeeLabel')}
                        </Typography>
                    </Stack>
                ) : hasCellFee ? (
                    <Typography variant="caption" color="error.main" sx={{ fontWeight: 700, pb: 1 }}>
                        {t('makePayment.cardFeeLabel')} {formatCurrency(cellFeeAmount)}
                    </Typography>
                ) : null}
            </Stack>
        );
    }

    // Default to "invoice" choice (Full Amount) - shows $0.00 when no open invoices
    const choice = row.amountChoice ?? 'invoice';

    const principalDigitCount =
        outstandingBalance != null && Number.isFinite(outstandingBalance)
            ? getRemainingPrincipalIntegerDigitCount(outstandingBalance)
            : 0;
    const restrictNineDigitAmount = principalDigitCount >= 10 && principalDigitCount <= 11;
    const consumerEffectiveMax = restrictNineDigitAmount
        ? Math.min(
              outstandingBalance != null ? getAmountMaxValue(outstandingBalance) : AMOUNT_MAX_VALUE,
              999_999_999.99,
          )
        : outstandingBalance != null
          ? getAmountMaxValue(outstandingBalance)
          : AMOUNT_MAX_VALUE;
    const consumerMaxLength = restrictNineDigitAmount
        ? 12
        : outstandingBalance != null
          ? getAmountMaxLength(outstandingBalance)
          : 11;
    const enteredAmount = parseFloat(String(row.paymentAmount ?? ''));
    const isOtherAmountExceedingBalance =
        choice === 'other' &&
        Number.isFinite(enteredAmount) &&
        enteredAmount > 0 &&
        outstandingBalance != null &&
        isAmountOverMax(enteredAmount, outstandingBalance);

    // Shared by onPaste/onChange below: warns (via toast) when the typed/pasted amount exceeds the
    // allowed max for this row, without altering the numeric comparison itself.
    const showAmountExceedsMaxToast = () => {
        dispatch(
            openToast({
                show: true,
                isAlertOpen: true,
                message:
                    outstandingBalance != null && outstandingBalance >= AMOUNT_PRINCIPAL_THRESHOLD
                        ? 'Amount Cannot Exceed 99,999,999.99'
                        : 'The Amount Cannot Exceed The Outstanding Balance.',
                severity: 'error',
            }),
        );
    };

    // Shared by onPaste/onChange below: writes the new amount into the row.
    const updatePaymentAmount = (value: string) => {
        setPaymentRows(prev => {
            const updated = [...prev];
            updated[index].paymentAmount = value;
            return updated;
        });
        // The user is retrying with a different amount, so a stale backend submit-error for this
        // row no longer reflects what's about to be submitted.
        setPaymentSubmitErrors(prev => {
            if (!prev[index]) return prev;
            const next = [...prev];
            next[index] = null;
            return next;
        });
    };

    // Shared by onPaste/onChange below: clears the paymentAmount row error once a positive amount is present.
    const clearPaymentAmountErrorIfPositive = (numVal: number) => {
        if (Number.isFinite(numVal) && numVal > 0) {
            setPaymentRowErrors(prev => {
                const next = [...prev];
                if (next[index]) next[index] = { ...next[index], paymentAmount: false };
                return next;
            });
        }
    };

    const showNoInvoiceNote = choice === 'invoice' && !hasContractInvoices;
    const showOtherAmountHint =
        choice === 'other' && !isBalanceFullyCovered && !isOtherAmountExceedingBalance && outstandingBalance != null;

    return (
        <Stack>
            <Stack direction={'row'} alignItems={isOtherAmountExceedingBalance ? 'flex-start' : 'center'}>
                <RadioGroup
                    row
                    value={choice}
                    onChange={e => {
                        const nextChoice = e.target.value as AmountChoice;
                        handleAmountChoiceChange(index, nextChoice);
                    }}
                    aria-label={t('makePayment.paymentAmountChoice')}
                    sx={{
                        flex: '0 0 auto',
                        '& .MuiFormControlLabel-root': {
                            mr: 1.25,
                        },
                    }}
                >
                    <FormControlLabel
                        value="invoice"
                        control={<CustomRadio />}
                        label={t('makePayment.payInvoiceAmount')}
                    />
                    <FormControlLabel
                        value="other"
                        disabled={isBalanceFullyCovered}
                        control={<CustomRadio />}
                        label={t('makePayment.payOtherAmount')}
                    />
                </RadioGroup>
                <Stack sx={{ flex: 1, minWidth: 120 }}>
                    <TextFieldComponent
                        ariaLabel="Payment Amount"
                        value={choice === 'invoice' ? String(displayDueAmount) : (row?.paymentAmount ?? '')}
                        inputMode="decimal"
                        disabled={choice === 'invoice'}
                        inputRef={el => setOtherAmountInputRef(index, el)}
                        error={!!paymentAmountError}
                        onKeyDown={e => {
                            const allowedKeys = [
                                'Backspace',
                                'Delete',
                                'Tab',
                                'Escape',
                                'Enter',
                                '.',
                                'ArrowLeft',
                                'ArrowRight',
                                'ArrowUp',
                                'ArrowDown',
                                'Home',
                                'End',
                            ];
                            if ((e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())) {
                                return;
                            }
                            const val = String(row?.paymentAmount ?? '');
                            const dot = val.indexOf('.');
                            const input = e.target as HTMLInputElement;
                            const start = input.selectionStart ?? 0;
                            const end = input.selectionEnd ?? 0;
                            if (/^[0-9]$/.test(e.key)) {
                                if (dot !== -1 && start > dot) {
                                    const decLen =
                                        val.slice(dot + 1).length -
                                        (end > dot ? Math.min(end - dot - 1, val.slice(dot + 1).length) : 0);
                                    const replaceDec = start > dot && end > dot && end > start;
                                    if (!replaceDec && decLen >= 2) {
                                        e.preventDefault();
                                        return;
                                    }
                                }
                                if (restrictNineDigitAmount) {
                                    const intEnd = dot === -1 ? val.length : dot;
                                    const intLen =
                                        val.slice(0, intEnd).length -
                                        (end <= intEnd && start < intEnd ? end - start : 0);
                                    const typingInInt = start <= (dot === -1 ? val.length : dot);
                                    if (typingInInt && intLen >= 9) {
                                        e.preventDefault();
                                        return;
                                    }
                                }
                            }
                            if (
                                e.key === '.' &&
                                val.includes('.') &&
                                !(start < end && val.slice(start, end).includes('.'))
                            ) {
                                e.preventDefault();
                                return;
                            }
                            if (allowedKeys.includes(e.key) || /^[0-9]$/.test(e.key)) {
                                return;
                            }
                            e.preventDefault();
                        }}
                        onPaste={e => {
                            e.preventDefault();
                            const pastedText = e.clipboardData.getData('text/plain').trim();
                            const value = normalizeConsumerLoanAmountInput(
                                pastedText,
                                restrictNineDigitAmount ? 9 : undefined,
                            );
                            if (value === '' || regex.paymentAmountRegExp.test(value)) {
                                if (value) {
                                    const numericValue = parseFloat(value);
                                    if (!isNaN(numericValue) && isAmountOverMax(numericValue, consumerEffectiveMax)) {
                                        showAmountExceedsMaxToast();
                                        return;
                                    }
                                }
                                updatePaymentAmount(value);
                                const numVal = parseFloat(value);
                                clearPaymentAmountErrorIfPositive(numVal);
                            } else {
                                dispatch(
                                    openToast({
                                        show: true,
                                        isAlertOpen: true,
                                        message: 'Invalid Characters. Only Numbers And Decimal Point Are Allowed.',
                                        severity: 'error',
                                    }),
                                );
                            }
                        }}
                        onChange={e => {
                            const normalized = normalizeConsumerLoanAmountInput(
                                e.target.value,
                                restrictNineDigitAmount ? 9 : undefined,
                            );
                            const value = normalized;
                            if (regex.paymentAmountRegExp.test(value) || value === '') {
                                if (value && value !== '.') {
                                    const numericValue = parseFloat(
                                        value.endsWith('.') ? value.slice(0, -1) || '0' : value,
                                    );
                                    if (!isNaN(numericValue) && isAmountOverMax(numericValue, consumerEffectiveMax)) {
                                        showAmountExceedsMaxToast();
                                        return;
                                    }
                                }
                                updatePaymentAmount(value);
                                const numVal = parseFloat(
                                    value.endsWith('.') ? value.slice(0, -1) || '0' : value || '0',
                                );
                                clearPaymentAmountErrorIfPositive(numVal);
                            }
                        }}
                        startIcon={<Typography color="textSecondary">$</Typography>}
                        maxLength={consumerMaxLength}
                    />
                    {/* {choice === 'other' && !isOtherAmountExceedingBalance && effectiveMaxBalance != null && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
                        {`Up to ${formatCurrency(effectiveMaxBalance)} available to pay`}
                    </Typography>
                )} */}

                    {isOtherAmountExceedingBalance && (
                        <Typography variant="caption" sx={{ color: 'warning.main', mt: 0.5, display: 'block' }}>
                            Amount exceeds your outstanding balance.
                        </Typography>
                    )}
                </Stack>
            </Stack>
            {showOtherAmountHint && (
                <Typography variant="caption" sx={{ color: 'info.main', mt: 0.5, lineHeight: 1.3, display: 'block' }}>
                    {t('makePayment.otherAmountHint')}
                </Typography>
            )}
            {showNoInvoiceNote && (
                <Typography
                    variant="caption"
                    sx={{ color: 'warning.main', mt: 0.5, lineHeight: 1.3, display: 'block' }}
                >
                    {t('makePayment.noInvoiceSelectOther')}
                </Typography>
            )}
        </Stack>
    );
};
